"use client"

import { useEffect, useRef, useState } from "react"
import {
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  parse,
  setMonth,
  setYear,
  startOfMonth,
} from "date-fns"
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"

import { cn } from "@/lib/utils"

interface DatePickerProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
  className?: string
  name?: string
  autocomplete?: string
}

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"]

const MONTHS = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
]

function generateYearRange(minYear: number, maxYear: number): number[] {
  const years: number[] = []
  for (let year = maxYear; year >= minYear; year--) {
    years.push(year)
  }
  return years
}

function displayFromIsoDate(value: string): {
  input: string
  month: Date | null
} {
  if (!value) return { input: "", month: null }
  const date = parse(value, "yyyy-MM-dd", new Date())
  if (!isValid(date)) return { input: "", month: null }
  return { input: format(date, "dd/MM/yyyy"), month: date }
}

export function DatePicker({
  value,
  onChange,
  placeholder = "DD/MM/AAAA",
  disabled = false,
  minDate,
  maxDate = new Date(),
  className,
  name,
  autocomplete,
}: DatePickerProps) {
  const [open, setOpen] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>(
    () => displayFromIsoDate(value).input
  )
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (value) {
      return parse(value, "yyyy-MM-dd", new Date())
    }
    // Default to 30 years ago for birth date picker
    const defaultDate = new Date()
    defaultDate.setFullYear(defaultDate.getFullYear() - 30)
    return defaultDate
  })
  const [valueSnapshot, setValueSnapshot] = useState(value)
  if (value !== valueSnapshot) {
    setValueSnapshot(value)
    const next = displayFromIsoDate(value)
    setInputValue(next.input)
    if (next.month) setCurrentMonth(next.month)
  }

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selectedDate = value ? parse(value, "yyyy-MM-dd", new Date()) : null

  // Generate year range (100 years back from maxDate)
  const maxYear = maxDate ? maxDate.getFullYear() : new Date().getFullYear()
  const minYear = minDate ? minDate.getFullYear() : maxYear - 100
  const years = generateYearRange(minYear, maxYear)

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }

    if (open) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [open])

  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd })

  const startPadding = getDay(monthStart)
  const paddingDays = Array(startPadding).fill(null)

  const handleDayClick = (day: Date) => {
    onChange(format(day, "yyyy-MM-dd"))
    setOpen(false)
  }

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev)
      newDate.setMonth(newDate.getMonth() - 1)
      return newDate
    })
  }

  const handleNextMonth = () => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev)
      newDate.setMonth(newDate.getMonth() + 1)
      return newDate
    })
  }

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentMonth((prev) => setMonth(prev, Number(e.target.value)))
  }

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentMonth((prev) => setYear(prev, Number(e.target.value)))
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value

    // Remove non-digit characters
    const digitsOnly = input.replace(/\D/g, "")

    // Auto-format as DD/MM/YYYY
    let formatted = ""
    if (digitsOnly.length > 0) {
      formatted = digitsOnly.substring(0, 2)
    }
    if (digitsOnly.length > 2) {
      formatted += "/" + digitsOnly.substring(2, 4)
    }
    if (digitsOnly.length > 4) {
      formatted += "/" + digitsOnly.substring(4, 8)
    }

    setInputValue(formatted)

    // Try to parse complete date
    if (digitsOnly.length === 8) {
      const day = digitsOnly.substring(0, 2)
      const month = digitsOnly.substring(2, 4)
      const year = digitsOnly.substring(4, 8)

      const parsedDate = parse(
        `${year}-${month}-${day}`,
        "yyyy-MM-dd",
        new Date()
      )

      if (isValid(parsedDate)) {
        const isBeforeMin = minDate && isBefore(parsedDate, minDate)
        const isAfterMax = maxDate && isAfter(parsedDate, maxDate)

        if (!isBeforeMin && !isAfterMax) {
          onChange(format(parsedDate, "yyyy-MM-dd"))
          setCurrentMonth(parsedDate)
        }
      }
    }
  }

  const handleInputBlur = () => {
    // Reset to current value if input is invalid
    if (value) {
      const date = parse(value, "yyyy-MM-dd", new Date())
      if (isValid(date)) {
        setInputValue(format(date, "dd/MM/yyyy"))
      }
    } else if (inputValue && inputValue.length < 10) {
      setInputValue("")
    }
  }

  const isDateDisabled = (day: Date) => {
    if (minDate && isBefore(day, minDate)) return true
    if (maxDate && isAfter(day, maxDate)) return true
    return false
  }

  return (
    <div ref={containerRef} className="relative">
      <div
        className={cn(
          "flex w-full items-center overflow-hidden rounded-xl border border-background-200 bg-white",
          "focus-within:border-primary-600 focus-within:ring-[3px] focus-within:ring-primary-500/50",
          disabled && "cursor-not-allowed opacity-50",
          className
        )}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={() => !disabled && setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          name={name}
          autoComplete={autocomplete}
          className={cn(
            "text-body flex-1 bg-transparent px-4 py-3 outline-none",
            "placeholder:text-text-400",
            disabled && "cursor-not-allowed"
          )}
        />
        <button
          type="button"
          onClick={() => !disabled && setOpen(!open)}
          className="px-3 py-3 transition-colors hover:bg-background-100"
          tabIndex={-1}
          disabled={disabled}
          aria-label="Abrir calendário"
        >
          <CalendarIcon className="h-5 w-5 text-text-400" />
        </button>
      </div>

      {open && (
        <div
          className={cn(
            "absolute top-full left-0 z-50 mt-2 w-[320px] rounded-xl border border-background-100 bg-white p-4 shadow-lg"
          )}
        >
          {/* Month/Year Selectors */}
          <div className="mb-4 flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="shrink-0 rounded-lg p-2 transition-colors hover:bg-background-100"
              aria-label="Mês anterior"
            >
              <ChevronLeft className="h-4 w-4 text-text-900" />
            </button>

            <select
              value={currentMonth.getMonth()}
              onChange={handleMonthChange}
              className={cn(
                "text-body-sm flex-1 px-2 py-2 font-medium text-text-900",
                "cursor-pointer rounded-lg border border-background-200 bg-white",
                "hover:border-background-300 focus:border-primary-600 focus:ring-2 focus:ring-primary-600 focus:outline-none"
              )}
            >
              {MONTHS.map((month, index) => (
                <option key={month} value={index}>
                  {month}
                </option>
              ))}
            </select>

            <select
              value={currentMonth.getFullYear()}
              onChange={handleYearChange}
              className={cn(
                "text-body-sm w-24 px-2 py-2 font-medium text-text-900",
                "cursor-pointer rounded-lg border border-background-200 bg-white",
                "hover:border-background-300 focus:border-primary-600 focus:ring-2 focus:ring-primary-600 focus:outline-none"
              )}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={handleNextMonth}
              className="shrink-0 rounded-lg p-2 transition-colors hover:bg-background-100"
              aria-label="Próximo mês"
            >
              <ChevronRight className="h-4 w-4 text-text-900" />
            </button>
          </div>

          {/* Weekday Headers */}
          <div className="mb-2 grid grid-cols-7 gap-1">
            {WEEKDAYS.map((day) => (
              <div
                key={day}
                className="text-caption flex h-8 items-center justify-center font-medium text-text-500"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {paddingDays.map((_, index) => (
              <div key={`padding-${index}`} className="h-8" />
            ))}
            {days.map((day) => {
              const isSelected = selectedDate && isSameDay(day, selectedDate)
              const isDisabled = isDateDisabled(day)

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => !isDisabled && handleDayClick(day)}
                  disabled={isDisabled}
                  className={cn(
                    "text-body-sm h-8 w-full rounded-lg transition-colors",
                    "text-text-900",
                    isSelected && "bg-primary-600 font-medium text-white",
                    !isSelected && !isDisabled && "hover:bg-primary-50",
                    isDisabled && "cursor-not-allowed opacity-30"
                  )}
                >
                  {format(day, "d")}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
